import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { ChevronLeft, ChevronRight, ShieldCheck, ShieldAlert, Ban, Search } from "lucide-react";
import { apiFetch } from "@/api";
import { Button, Spinner, cn } from "@/ui";

const BASE = "/developer/trial";
const card = "rounded-xl border border-border p-5";

const EVENT_STYLE = {
  ACTIVATE: "text-primary",
  OVERRIDE: "text-primary",
  DENY: "text-muted-foreground",
  BLOCK: "text-destructive",
  UNBLOCK: "text-muted-foreground",
  CHECK: "text-muted-foreground",
};

function fmt(ts) {
  if (!ts) return "—";
  try { return new Date(ts).toLocaleString(); } catch { return ts; }
}

function StatusPill({ status }) {
  const on = status === "trialing" || status === "active";
  return (
    <span data-testid="trial-status" className={cn("rounded-full px-2.5 py-0.5 text-xs font-semibold capitalize",
      on ? "bg-primary text-primary-foreground" : "bg-secondary text-muted-foreground")}>{status}</span>
  );
}

function AuditList({ rows, emptyText }) {
  if (!rows || rows.length === 0) return <p className="text-sm text-muted-foreground">{emptyText}</p>;
  return (
    <div className="flex flex-col gap-2">
      {rows.map((a, i) => (
        <div key={a.id || i} data-testid={`trial-audit-row-${i}`} className="flex items-start justify-between gap-3 border-b border-border pb-2 text-sm last:border-0">
          <div className="min-w-0">
            <div className={cn("font-semibold", EVENT_STYLE[a.event] || "")}>{a.event} · {a.reason_code || "—"}</div>
            <div className="truncate text-xs text-muted-foreground">
              {a.decision}{a.admin_name ? ` · by ${a.admin_name}` : ""}{a.note ? ` · ${a.note}` : ""}
              {a.device_hash_short ? ` · dev ${a.device_hash_short}` : ""}{a.platform ? ` · ${a.platform}` : ""}
            </div>
          </div>
          <span className="shrink-0 text-xs text-muted-foreground">{fmt(a.at)}</span>
        </div>
      ))}
    </div>
  );
}

export function TrialCenter() {
  const [q, setQ] = useState("");
  const [users, setUsers] = useState(null);
  const [ov, setOv] = useState(null);
  const [feed, setFeed] = useState([]);
  const [loading, setLoading] = useState(false);

  const loadFeed = useCallback(async () => {
    try { setFeed((await apiFetch(`${BASE}/audit?limit=50`)).audit); } catch (e) { toast(e.message); }
  }, []);
  useEffect(() => { loadFeed(); }, [loadFeed]);

  const search = async () => {
    try { const d = await apiFetch(`/developer/users${q ? `?q=${encodeURIComponent(q)}` : ""}`); setUsers(d.users || []); }
    catch (e) { toast(e.message); }
  };
  const open = async (id) => {
    setLoading(true);
    try { setOv(await apiFetch(`${BASE}/overview/${id}`)); } catch (e) { toast(e.message); } finally { setLoading(false); }
  };
  const refresh = async () => { if (ov) await open(ov.account_id); await loadFeed(); };

  const override = async () => {
    const reason = window.prompt("Reason for granting another free trial (e.g. shared family device):", "");
    if (!reason) return;
    try { await apiFetch(`${BASE}/override`, { method: "POST", body: { account_id: ov.account_id, reason } }); toast("Trial granted."); refresh(); }
    catch (e) { toast(e.message); }
  };
  const block = async () => {
    const reason = window.prompt("Reason for blocking this device from trials:", "") || "";
    if (!window.confirm("Block this device from future free trials and end the current trial?")) return;
    try { await apiFetch(`${BASE}/block`, { method: "POST", body: { account_id: ov.account_id, reason } }); toast("Device blocked."); refresh(); }
    catch (e) { toast(e.message); }
  };
  const unblock = async () => {
    try { await apiFetch(`${BASE}/unblock`, { method: "POST", body: { account_id: ov.account_id } }); toast("Device unblocked."); refresh(); }
    catch (e) { toast(e.message); }
  };

  if (ov) {
    const dev = ov.device || null;
    return (
      <div className="flex flex-col gap-4">
        <button className="flex items-center gap-1 text-sm" onClick={() => setOv(null)}><ChevronLeft className="h-4 w-4" /> Back</button>
        {loading ? <div className="flex justify-center py-6"><Spinner className="h-5 w-5 text-foreground" /></div> : null}
        <div className={cn(card, "flex flex-col gap-2")}>
          <div className="flex items-center justify-between gap-2">
            <div className="text-lg font-bold">{ov.name || "User"}</div>
            <StatusPill status={ov.trial.status} />
          </div>
          <div className="text-sm text-muted-foreground">Trial start: {fmt(ov.trial.trial_start)}</div>
          <div className="text-sm text-muted-foreground">Trial end: {fmt(ov.trial.trial_end)}</div>
          <div className="text-xs text-muted-foreground">Last decision: <span className="font-mono">{ov.trial.reason || "—"}</span></div>
        </div>

        <div className={cn(card, "flex flex-col gap-2")} data-testid="trial-device">
          <div className="text-xs font-bold uppercase tracking-wider text-muted-foreground">Device (abuse-prevention ledger)</div>
          {dev ? (
            <>
              <div className="text-sm">Device: <span className="font-mono">{dev.device_hash_short || (dev.device_hash || "").slice(0, 12)}…</span> · {dev.platform || "—"}</div>
              <div className="text-sm text-muted-foreground">Trial used on this device: <span className="font-semibold text-foreground">{dev.consumed ? "Yes" : "No"}</span> · first seen {fmt(dev.first_seen_at)}</div>
              <div className="text-sm text-muted-foreground">Blocked: <span className={cn("font-semibold", dev.blocked ? "text-destructive" : "text-foreground")}>{dev.blocked ? `Yes${dev.blocked_reason ? ` — ${dev.blocked_reason}` : ""}` : "No"}</span></div>
              {dev.last_override_by ? <div className="text-xs text-muted-foreground">Last override by {dev.last_override_by}{dev.last_override_reason ? ` — ${dev.last_override_reason}` : ""} ({fmt(dev.last_override_at)})</div> : null}
            </>
          ) : (
            <p className="text-sm text-muted-foreground">No device on record (web sign-up or no device id). Trials are mobile-only, so web accounts have no device ledger.</p>
          )}
          <div className="mt-1 flex flex-wrap gap-2">
            <Button data-testid="trial-override" className="py-2 text-sm" onClick={override}><ShieldCheck className="h-4 w-4" /> Grant another trial</Button>
            {dev && dev.blocked
              ? <Button data-testid="trial-unblock" variant="secondary" className="py-2 text-sm" onClick={unblock}><ShieldAlert className="h-4 w-4" /> Unblock device</Button>
              : <Button data-testid="trial-block" variant="secondary" className="py-2 text-sm" onClick={block}><Ban className="h-4 w-4" /> Block device</Button>}
          </div>
        </div>

        <div className={cn(card, "flex flex-col gap-3")}>
          <div className="text-xs font-bold uppercase tracking-wider text-muted-foreground">Trial history</div>
          <AuditList rows={ov.audit} emptyText="No trial events for this user." />
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-wrap items-center gap-2">
        <input data-testid="trial-search" value={q} onChange={(e) => setQ(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter") search(); }}
          placeholder="Search a user by name…" className="flex-1 rounded-lg border border-input bg-background px-3 py-2 text-sm outline-none" />
        <Button data-testid="trial-search-btn" variant="secondary" className="py-2 text-sm" onClick={search}><Search className="h-4 w-4" /> Search</Button>
      </div>
      {users !== null ? (
        users.length === 0 ? <p className="text-sm text-muted-foreground">No users match.</p> : (
          <div className="flex flex-col gap-2">
            {users.map((u) => (
              <button key={u.id} data-testid={`trial-user-${u.id}`} onClick={() => open(u.id)} className={cn(card, "flex items-center justify-between gap-3 text-left")}>
                <div className="min-w-0">
                  <div className="font-semibold">{u.name || "User"}</div>
                  <div className="truncate text-sm text-muted-foreground">{u.subscription || "—"}{u.learning_level ? ` · ${u.learning_level}` : ""}</div>
                </div>
                <ChevronRight className="h-5 w-5 shrink-0 text-muted-foreground" />
              </button>
            ))}
          </div>
        )
      ) : null}

      <div className={cn(card, "flex flex-col gap-3")} data-testid="trial-recent">
        <div className="text-xs font-bold uppercase tracking-wider text-muted-foreground">Recent trial activity</div>
        <p className="text-[11px] text-muted-foreground">Server-side trial checks, activations, denials, overrides and blocks. Trials are mobile-only; the backend is the sole authority.</p>
        <AuditList rows={feed} emptyText="No trial activity yet." />
      </div>
    </div>
  );
}
